/**
 * infinite-scroll.js  (v3)
 *
 * Progressive-enhancement infinite scroll for the collection product grid.
 *
 * v3 changes — fixes infinite scroll dying as soon as the customer sorts or
 * filters:
 *   1. RE-BINDS AFTER AJAX UPDATES. Sorting or filtering a collection does not
 *      reload the page: the theme fetches the new results and swaps the product
 *      grid, the pagination block and the sentinel for freshly rendered HTML.
 *      v2 bound its IntersectionObserver once, on page load, so after any sort
 *      or filter it was left watching a node that no longer existed in the
 *      document. Scrolling then loaded nothing and the customer was stranded on
 *      a single page of results. A debounced DOM watcher now notices that the
 *      sentinel it was observing has been swapped out, tears the old session
 *      down and binds a new one to the replacement markup.
 *   2. THE NUMBERED PAGINATION IS A REAL FALLBACK AGAIN. [data-infinite-enabled]
 *      on the root is what hides the numbered pagination (see the CSS in
 *      sections/collection.liquid). v2 set it once and never cleared it, so any
 *      time infinite scroll stopped the customer was left with no way forward
 *      at all: no more products and no page links either. It is now set only
 *      while an observer is genuinely live, and cleared again the moment
 *      infinite scroll stops because something went wrong.
 *   3. EXPLICIT END OF LIST. When every page really has been loaded, a short
 *      closing line is shown, so "no more products" reads as the end of the
 *      collection rather than as a broken page.
 *
 * Unchanged from v2:
 *   - PREFETCH AHEAD. As soon as one page is appended the next is fetched in
 *     the background, so appending is usually instant.
 *   - WIDER TRIGGER. Loading starts 1500px before the sentinel is on screen.
 *   - VISIBLE LOADING STATE, delayed 120ms so the instant path never flashes it.
 *   - AUTO-CONTINUE when a page lands and the sentinel is still on screen.
 *   - The URL bar keeps updating (history.replaceState) so every "page" stays
 *     independently linkable and shareable.
 *   - If JavaScript fails or IntersectionObserver isn't supported, the normal
 *     numbered pagination links underneath still work exactly as before.
 *
 * Scope: only activates inside an element carrying [data-infinite-scroll-root],
 * which is only rendered when the "Enable infinite scroll" section setting is on.
 */
(function () {
  'use strict';

  var LOOKAHEAD_PX = 1500;
  var SPINNER_DELAY_MS = 120;
  var SWEEP_DEBOUNCE_MS = 60;

  /* One live session per root element. A session owns exactly one sentinel;
     when that sentinel leaves the document we know the markup underneath us
     was replaced (sort, filter, pagination swap) and the session is dead. */
  var sessions = typeof WeakMap === 'function' ? new WeakMap() : null;
  var sweepScheduled = false;

  function injectKeyframesOnce() {
    if (document.getElementById('infinite-scroll-keyframes')) return;
    var style = document.createElement('style');
    style.id = 'infinite-scroll-keyframes';
    style.textContent =
      '@keyframes infScrollSpin{to{transform:rotate(360deg)}}' +
      '@media (prefers-reduced-motion:reduce){[data-infinite-spin]{animation-duration:2.4s}}';
    document.head.appendChild(style);
  }

  function buildStatusRow() {
    injectKeyframesOnce();
    var el = document.createElement('div');
    el.setAttribute('data-infinite-status', '');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.style.cssText =
      'display:none;justify-content:center;align-items:center;gap:.6em;' +
      'padding:2.5em 0;opacity:.6;font-size:.85em;width:100%;';
    el.innerHTML =
      '<span data-infinite-spin style="display:inline-block;width:1.1em;height:1.1em;' +
      'border:2px solid currentColor;border-right-color:transparent;border-radius:50%;' +
      'animation:infScrollSpin .7s linear infinite;"></span>' +
      '<span>Loading more…</span>';
    return el;
  }

  function buildEndRow() {
    var el = document.createElement('div');
    el.setAttribute('data-infinite-end', '');
    el.style.cssText =
      'display:flex;justify-content:center;align-items:center;' +
      'padding:2.5em 0;opacity:.55;font-size:.85em;width:100%;text-align:center;';
    el.textContent = "That's everything in this collection.";
    return el;
  }

  function removeMatching(root, selector) {
    var nodes = root.querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].parentNode) nodes[i].parentNode.removeChild(nodes[i]);
    }
  }

  /**
   * Bind a fresh infinite-scroll session to whatever markup is in this root
   * right now. Returns a session handle, or null when there is nothing to do
   * (no grid, no next page, or the browser can't support it) — in which case
   * the numbered pagination is left visible so the customer can still browse.
   */
  function startSession(root) {
    var grid = root.querySelector('[data-grid-large]');
    var paginationWrap = root.querySelector('[data-infinite-pagination]');
    var sentinel = root.querySelector('[data-infinite-sentinel]');

    if (!grid || !paginationWrap || !sentinel ||
        !('IntersectionObserver' in window) || typeof window.fetch !== 'function') {
      root.removeAttribute('data-infinite-enabled');
      return null;
    }

    function getNextUrl() {
      var nextLink = paginationWrap.querySelector('[data-pagination-next]');
      return nextLink ? nextLink.getAttribute('href') : null;
    }

    /* Nothing after this page — e.g. the customer deep-linked the last page,
       or a filter narrowed the results to one page. Hand them back the
       numbered pagination (it still carries the "previous" links). */
    if (!getNextUrl()) {
      root.removeAttribute('data-infinite-enabled');
      return null;
    }

    /* Clear anything a previous session left behind in this root. */
    removeMatching(root, '[data-infinite-status]');
    removeMatching(root, '[data-infinite-end]');
    sentinel.removeAttribute('hidden');

    var observer = null;
    var appending = false;
    var exhausted = false;
    var torndown = false;
    var prefetch = null; // { url: String, promise: Promise<Document|null> }

    var statusRow = buildStatusRow();
    sentinel.parentNode.insertBefore(statusRow, sentinel);

    function showLoading(on) {
      statusRow.style.display = on ? 'flex' : 'none';
    }

    function detachObserver() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }

    function removeStatusRow() {
      showLoading(false);
      if (statusRow.parentNode) statusRow.parentNode.removeChild(statusRow);
    }

    /**
     * reason 'end'   — every page has genuinely been loaded. Say so, and leave
     *                  the numbered pagination hidden: there is nowhere to go.
     * reason 'error' — we could not continue (network, unexpected markup).
     *                  Give the numbered pagination back so the customer is
     *                  never left staring at a grid that just stops.
     */
    function stop(reason) {
      exhausted = true;
      detachObserver();
      removeStatusRow();
      sentinel.setAttribute('hidden', 'hidden');

      if (reason === 'error') {
        root.removeAttribute('data-infinite-enabled');
        return;
      }

      if (!root.querySelector('[data-infinite-end]') && sentinel.parentNode) {
        sentinel.parentNode.insertBefore(buildEndRow(), sentinel);
      }
    }

    /* Warm the next page in the background. Cheap: one GET the browser would
       have made anyway, just earlier. */
    function startPrefetch() {
      if (torndown || exhausted) { prefetch = null; return; }
      var url = getNextUrl();
      if (!url) { prefetch = null; return; }
      if (prefetch && prefetch.url === url) return;

      prefetch = {
        url: url,
        promise: window.fetch(url, { credentials: 'same-origin' })
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.text();
          })
          .then(function (html) {
            return new DOMParser().parseFromString(html, 'text/html');
          })
          .catch(function () {
            return null; // fall back to the visible pagination links
          })
      };
    }

    function appendNext() {
      if (torndown || appending || exhausted) return;

      var url = getNextUrl();
      if (!url) { stop('end'); return; }

      if (!prefetch || prefetch.url !== url) startPrefetch();
      if (!prefetch) { stop('error'); return; }

      appending = true;
      root.setAttribute('data-infinite-loading', 'true');

      var pending = prefetch.promise;
      var settled = false;
      pending.then(function () { settled = true; });
      window.setTimeout(function () {
        if (!settled && appending && !exhausted && !torndown) showLoading(true);
      }, SPINNER_DELAY_MS);

      pending.then(function (doc) {
        if (torndown) return;
        if (!doc) { stop('error'); return; }

        var newRoot = doc.querySelector('[data-infinite-scroll-root]');
        if (!newRoot) { stop('error'); return; }

        var newGrid = newRoot.querySelector('[data-grid-large]');
        var newPagination = newRoot.querySelector('[data-infinite-pagination]');

        /* No grid in the response means we were served something we don't
           understand. Don't append nothing and silently call it the end. */
        if (!newGrid) { stop('error'); return; }

        var fragment = document.createDocumentFragment();
        while (newGrid.firstChild) {
          fragment.appendChild(newGrid.firstChild);
        }
        grid.appendChild(fragment);

        /* Always replace the pagination block, even when the new page has
           none: leaving the old one in place would keep a stale "next" link
           pointing at the page we just loaded, and we would fetch it forever. */
        paginationWrap.innerHTML = newPagination ? newPagination.innerHTML : '';

        try {
          history.replaceState(history.state, '', url);
        } catch (err) {
          /* no-op: URL sync is a nice-to-have, never block on it */
        }

        root.dispatchEvent(new CustomEvent('infinite-scroll:appended', { bubbles: true }));
      }).catch(function () {
        /* leave the numbered pagination links intact as a fallback */
      }).then(function () {
        appending = false;
        showLoading(false);
        root.removeAttribute('data-infinite-loading');
        prefetch = null;

        if (torndown || exhausted) return;
        if (!getNextUrl()) { stop('end'); return; }

        startPrefetch();

        /* If the customer is still sitting at the bottom (tall viewport, or a
           page that rendered short), keep going rather than making them nudge
           the scroll wheel again. Uses the real viewport edge, not the
           lookahead margin, so this can't run away on its own. */
        window.requestAnimationFrame(function () {
          if (torndown || exhausted || appending) return;
          var rect = sentinel.getBoundingClientRect();
          if (rect.top <= window.innerHeight) appendNext();
        });
      });
    }

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) appendNext();
        });
      },
      { rootMargin: LOOKAHEAD_PX + 'px 0px ' + LOOKAHEAD_PX + 'px 0px' }
    );

    observer.observe(sentinel);
    root.setAttribute('data-infinite-enabled', 'true');

    /* Warm page 2 straight away so the very first append is instant too. */
    startPrefetch();

    return {
      /* Healthy for as long as the markup we bound to is still on the page.
         Our own appends never replace the sentinel, so this stays true while
         scrolling and only flips when the theme swaps the grid out from under
         us (sort, filter, or any other AJAX re-render). */
      isHealthy: function () {
        return !torndown && document.contains(sentinel);
      },
      teardown: function () {
        torndown = true;
        detachObserver();
        prefetch = null;
        removeStatusRow();
      }
    };
  }

  function ensure(root) {
    if (!sessions) {
      /* No WeakMap (very old browser): bind once and leave it. Worst case is
         the v2 behaviour, which is what this browser would have had anyway. */
      if (!root.getAttribute('data-infinite-bound')) {
        root.setAttribute('data-infinite-bound', 'true');
        startSession(root);
      }
      return;
    }

    var session = sessions.get(root);
    if (session && session.isHealthy()) return;
    if (session) session.teardown();

    var fresh = startSession(root);
    if (fresh) {
      sessions.set(root, fresh);
    } else {
      sessions['delete'](root);
    }
  }

  function sweep() {
    var roots = document.querySelectorAll('[data-infinite-scroll-root]');
    for (var i = 0; i < roots.length; i++) {
      ensure(roots[i]);
    }
  }

  function scheduleSweep() {
    if (sweepScheduled) return;
    sweepScheduled = true;
    window.setTimeout(function () {
      sweepScheduled = false;
      sweep();
    }, SWEEP_DEBOUNCE_MS);
  }

  function boot() {
    sweep();

    /* Watch for the theme replacing the collection markup. Sorting and
       filtering both re-render the grid in place without a page load, and
       neither fires an event this script can rely on across theme versions,
       so we watch the document instead. The check itself is trivial (one
       document.contains per root) and debounced, and our own appends leave the
       sentinel in place, so scrolling does not cause rebinding. */
    if (typeof window.MutationObserver === 'function') {
      new MutationObserver(scheduleSweep).observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    /* Theme editor: re-render of this section. */
    document.addEventListener('shopify:section:load', scheduleSweep);

    /* Browser back/forward between sorted or filtered views. */
    window.addEventListener('popstate', scheduleSweep);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
